import { useQuery } from "@apollo/client";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Flex,
  LoadingOverlay,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Group,
} from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconCaretDown } from "@tabler/icons";
import { GET_DROPOFF } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import React, { useEffect, useState } from "react";
import {
  CurrencyDollar,
  CreditCard,
  Receipt,
  Category,
} from "tabler-icons-react";
import { formatNumber } from "utiles/url";

const ManageDropOffModal = ({ editId, setOpenedEdit }) => {
  const [dropOffData, setDropOffData] = useState(null);
  const [collapse, setCollapse] = useState(null);

  const { loading, refetch } = useQuery(GET_DROPOFF, {
    variables: { id: editId },
    onCompleted(data) {
      setDropOffData(data);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get drop-off",
      });
    },
  });

  useEffect(() => {
    refetch();
  }, []);

  return (
    <>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <ScrollArea style={{ height: "90vh", marginY: 20 }}>
        <Tabs defaultValue="driver" variant="outline" radius="md" color="blue">
          {/* ------------------- DRIVER INFO TAB ------------------- */}
          <Tabs.List>
            <Tabs.Tab value="driver">Driver Information</Tabs.Tab>
            <Tabs.Tab value="retailer">Retailer Information</Tabs.Tab>
            <Tabs.Tab value="products">Product Details</Tabs.Tab>
            <Tabs.Tab value="payment">Payment Information</Tabs.Tab>
          </Tabs.List>

          {/* DRIVER */}
          <Tabs.Panel value="driver" pt="md">
            <Card shadow="sm" p="lg" radius="md" withBorder>
              {dropOffData && dropOffData.dropoff.driver ? (
                <>
                  <Group position="apart" mt="md" mb="xs">
                    <Text weight={500}>
                      Name: {dropOffData.dropoff.driver.user.name}
                    </Text>
                    <Badge color="green" variant="light">
                      City: {dropOffData.dropoff.driver.city}
                    </Badge>
                  </Group>

                  <Text size="sm" color="dimmed">
                    Phone: {dropOffData.dropoff.driver.user.phone}
                  </Text>
                </>
              ) : (
                <Button
                  variant="light"
                  color="blue"
                  fullWidth
                  mt="md"
                  radius="md"
                >
                  Driver Not Assigned
                </Button>
              )}
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="retailer" pt="md">
            {dropOffData &&
              dropOffData.dropoff.dropoff_order.map((data, idx) => (
                <Card
                  key={idx}
                  shadow="sm"
                  p="lg"
                  my="md"
                  radius="md"
                  withBorder
                >
                  <Group position="apart" mt="md" mb="xs">
                    <Text weight={500}>
                      Retailer:{" "}
                      {data.order.retailer
                        ? data.order.retailer.name
                        : "Unknown"}
                    </Text>
                    <Badge
                      color={data.received ? "green" : "red"}
                      variant="light"
                    >
                      {data.received ? "Received" : "Not Received"}
                    </Badge>
                    <Badge color="yellow" variant="light">
                      Items: {data.order.items.length}
                    </Badge>
                  </Group>

                  <Text size="sm" color="dimmed">
                    Phone:{" "}
                    {data.order.retailer
                      ? data.order.retailer.contact_phone
                      : "Unknown"}
                    <br />
                    Address:{" "}
                    {data.order.retailer
                      ? data.order.retailer.address
                      : "Unknown"}
                    <br />
                    City:{" "}
                    {data.order.retailer ? data.order.retailer.city : "Unknown"}
                  </Text>
                </Card>
              ))}
          </Tabs.Panel>

          <Tabs.Panel value="products" pt="md">
            {dropOffData &&
              dropOffData.dropoff.dropoff_order.map((data, idx) => (
                <Group key={idx}  my="sm">
                  <Text weight={600} size="lg">
                    Total Price:
                  </Text>
                  <Text weight={700} size="lg">
                    {formatNumber(data.order?.total_price || 0)}
                  </Text>
                </Group>
              ))}

            {dropOffData &&
              dropOffData.dropoff.dropoff_order.map((data, idx) => (
                <Card
                  key={idx}
                  shadow="sm"
                  p="lg"
                  my="md"
                  radius="md"
                  withBorder
                >
                  <ScrollArea style={{ width: "40vw", margin: "auto" }}>
                    <Flex
                      mih={150}
                      gap="md"
                      justify="center"
                      align="center"
                      direction="row"
                      wrap="wrap"
                    >
                      {data.order.items.map((item, id) => {
                        const product = item.product_sku.product;
                        const firstImage = product.images?.[0]?.original_url;

                        return (
                          <Card
                            key={id}
                            shadow="lg"
                            p="lg"
                            my="md"
                            radius="md"
                            style={{ width: 200 }}
                            withBorder
                          >
                            <Center>
                              <Avatar
                                size="lg"
                                src={firstImage}
                                alt={product.name}
                              />
                            </Center>

                            <Text mt="sm" weight={500} lineClamp={2}>
                              {product.name}
                            </Text>

                            <Group mt="xs" spacing="xs">
                              <Text size="sm" color="dimmed">
                                Quantity:
                              </Text>
                              <Text size="sm" weight={500}>
                                {item.quantity}
                              </Text>
                            </Group>

                            <Group mt="xs" spacing="xs">
                              <Text size="sm" color="dimmed">
                                Price:
                              </Text>
                              <Text size="sm" weight={500}>
                                {formatNumber(item.product_sku.sell_price || 0)}
                              </Text>
                            </Group>
                          </Card>
                        );
                      })}
                    </Flex>
                  </ScrollArea>
                </Card>
              ))}
          </Tabs.Panel>

          <Tabs.Panel value="payment" pt="md">
            {dropOffData &&
              dropOffData.dropoff.dropoff_order.map((data, idx) => (
                <Card
                  key={idx}
                  shadow="sm"
                  p="lg"
                  my="md"
                  radius="md"
                  withBorder
                >
                  <Stack spacing="sm">
                    <Group>
                      <Text weight={500}>Payment Information</Text>
                    </Group>

                    {data.order.transactions &&
                    data.order.transactions.length > 0 ? (
                      data.order.transactions.map((transaction, tIdx) => (
                        <Stack key={tIdx} spacing="sm">
                          <Group>
                            <Group spacing="xs">
                              <CurrencyDollar size={20} color="#4dabf7" />
                              <Text size="sm" color="dimmed">
                                Total Amount:
                              </Text>
                            </Group>
                            <Text weight={400} size="md">
                              {formatNumber(transaction.amount) || "N/A"}
                            </Text>
                          </Group>

                          <Group>
                            <Group spacing="xs">
                              <CreditCard size={20} color="#4dabf7" />
                              <Text size="sm" color="dimmed">
                                Payment Method:
                              </Text>
                            </Group>
                            <Text weight={400} size="md">
                              {transaction.payment_method || "N/A"}
                            </Text>
                          </Group>

                          <Group>
                            <Group spacing="xs">
                              <Receipt size={20} color="#4dabf7" />
                              <Text size="sm" color="dimmed">
                                Transaction Reference:
                              </Text>
                            </Group>
                            <Text weight={400} size="md">
                              {transaction.txn_ref || "N/A"}
                            </Text>
                          </Group>

                          <Group>
                            <Group spacing="xs">
                              <Category size={20} color="#4dabf7" />
                              <Text size="sm" color="dimmed">
                                Transaction Type:
                              </Text>
                            </Group>
                            <Text weight={400} size="md">
                              {transaction.type || "N/A"}
                            </Text>
                          </Group>
                        </Stack>
                      ))
                    ) : (
                      <Text size="sm" color="dimmed">
                        No transactions available
                      </Text>
                    )}
                  </Stack>
                </Card>
              ))}
          </Tabs.Panel>
        </Tabs>
      </ScrollArea>
    </>
  );
};

export default ManageDropOffModal;
